import { useCallback, useMemo, useRef, useState } from "react";
import { ColDef, ColumnState, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { blueButtonClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import ModalWrapper from "src/components/common/modal/ModalWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { showWarning } from "src/utils/toast/toastUtil";

import {
  defaultPRD2050_R01LotDto,
  PRD2050_R01LotDto,
} from "./Dto_PRD2050_R01";
import { usePRD2050_R01Store } from "./Store_PRD2050_R01";

const PRD2050_R01_MODAL_COLUMN_STATE_KEY =
  "PRD2050_R01-material-lot-modal-column-state-v2";

interface PRD2050_R01LotPrefixViolation {
  missingLot: PRD2050_R01LotDto;
  selectedLaterLot: PRD2050_R01LotDto;
}

/** FIFO를 독립 적용할 창고·품목 그룹 키를 만든다. */
const getPRD2050_R01LotGroupKey = (lot: PRD2050_R01LotDto) =>
  JSON.stringify([lot.warehouseCd, lot.itemCd]);

/** 그룹 내 선택 결과가 canonical 목록의 앞부분(prefix)인지 검사한다. */
const getPRD2050_R01LotPrefixViolation = (
  canonicalGroupList: PRD2050_R01LotDto[],
  selectedLotNoSet: Set<string>,
): PRD2050_R01LotPrefixViolation | undefined => {
  let missingLot: PRD2050_R01LotDto | undefined;

  for (const lot of canonicalGroupList) {
    if (!selectedLotNoSet.has(lot.lotNo)) {
      missingLot ??= lot;
      continue;
    }
    if (missingLot) {
      return { missingLot, selectedLaterLot: lot };
    }
  }

  return undefined;
};

/** 모든 그룹 중 FIFO prefix 규칙을 처음 위반한 선택을 찾는다. */
const getFirstPRD2050_R01LotPrefixViolation = (
  canonicalLotGroupMap: Map<string, PRD2050_R01LotDto[]>,
  selectedLotNoSet: Set<string>,
): PRD2050_R01LotPrefixViolation | undefined => {
  for (const canonicalGroupList of Array.from(canonicalLotGroupMap.values())) {
    const violation = getPRD2050_R01LotPrefixViolation(
      canonicalGroupList,
      selectedLotNoSet,
    );
    if (violation) return violation;
  }

  return undefined;
};

/**
 * 선택한 작업지시 품목의 BOM에 속하는 가용 재고 LOT을 다중 선택한다.
 * 선택 결과는 서버에 즉시 저장하지 않고 하단 상세에 신규 행으로만 추가한다.
 */
export const Modal_PRD2050_R01 = () => {
  const {
    PRD2050_R01DetailList,
    PRD2050_R01ModalList,
    insertPRD2050_R01DetailList,
    setIsModalVisible,
  } = usePRD2050_R01Store();
  const gridRef = useRef<AgGridReact<PRD2050_R01LotDto>>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [selectedLotNoList, setSelectedLotNoList] = useState<string[]>([]);

  /** 공통 행 선택 처리에서 전달받은 LOT 번호를 중복 없이 선택 목록에 추가한다. */
  const addSelectedLotNoList = useCallback((lotNoList: string[]) => {
    setSelectedLotNoList((selectedList) =>
      Array.from(new Set([...selectedList, ...lotNoList])),
    );
  }, []);

  /** 선택 해제된 LOT 번호를 모달 선택 목록에서 제거한다. */
  const removeSelectedLotNoList = useCallback((lotNoList: string[]) => {
    setSelectedLotNoList((selectedList) =>
      selectedList.filter((lotNo) => !lotNoList.includes(lotNo)),
    );
  }, []);

  /** 모달을 닫거나 선택 결과를 반영한 뒤 임시 선택 상태를 초기화한다. */
  const initSelectedLotNoList = useCallback(() => {
    setSelectedLotNoList([]);
  }, []);

  /**
   * 서버가 창고·품목과 LOT 번호 오름차순으로 반환한 원본 순서를
   * 그룹별 canonical FIFO 순서로 보관한다. AG Grid 표시 정렬은 이 순서에 영향을 주지 않는다.
   */
  const canonicalLotGroupMap = useMemo(() => {
    const groupMap = new Map<string, PRD2050_R01LotDto[]>();

    PRD2050_R01ModalList.forEach((lot) => {
      const groupKey = getPRD2050_R01LotGroupKey(lot);
      const groupList = groupMap.get(groupKey);
      if (groupList) {
        groupList.push(lot);
      } else {
        groupMap.set(groupKey, [lot]);
      }
    });

    return groupMap;
  }, [PRD2050_R01ModalList]);

  const { getRowId, createRowClassRules, paddedGridData } =
    useGridSettingContent<PRD2050_R01LotDto>({
      gridRef,
      key: "lotNo",
      gridData: PRD2050_R01ModalList,
      defaultGridData: defaultPRD2050_R01LotDto,
      selectedRow: selectedLotNoList,
    });

  const columnDefs = useMemo<ColDef<PRD2050_R01LotDto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        colId: "logicCenter",
        headerName: "물류센터",
        field: "logicCenter",
        width: 100,
      },
      {
        colId: "warehouseNm",
        headerName: "창고명",
        field: "warehouseNm",
        width: 110,
      },
      { colId: "itemCd", headerName: "품목코드", field: "itemCd", width: 130 },
      { colId: "itemNm", headerName: "품명", field: "itemNm", width: 150 },
      { colId: "itemSpec", headerName: "규격", field: "itemSpec", width: 130 },
      {
        colId: "itemStatus",
        headerName: "등급",
        field: "itemStatus",
        width: 60,
      },
      { colId: "itemUnit", headerName: "단위", field: "itemUnit", width: 60 },
      { colId: "lotNo", headerName: "LOT 번호", field: "lotNo", width: 150 },
      {
        colId: "lotQty",
        headerName: "현재고",
        field: "lotQty",
        width: 90,
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "",
      },
    ],
    [],
  );

  const rowClassRules = useMemo(
    () =>
      createRowClassRules<PRD2050_R01LotDto>({
        idKey: "lotNo",
        selectedList: selectedLotNoList,
        deletedList: [],
      }),
    [createRowClassRules, selectedLotNoList],
  );

  /**
   * 그룹별 canonical FIFO prefix를 유지하는 경우에만 클릭한 LOT의 선택을 변경한다.
   * 화면에서 사용자가 정렬한 표시 순서나 AG Grid rowIndex는 검증에 사용하지 않는다.
   */
  const handleRowClick = useCallback(
    (event: RowClickedEvent<PRD2050_R01LotDto>) => {
      if (event.node.rowPinned === "bottom" || !event.data?.lotNo) return;

      const clickedLot = event.data;
      const canonicalGroupList =
        canonicalLotGroupMap.get(getPRD2050_R01LotGroupKey(clickedLot)) ?? [];
      const isSelected = selectedLotNoList.includes(clickedLot.lotNo);
      const nextSelectedLotNoSet = new Set(selectedLotNoList);

      if (isSelected) {
        nextSelectedLotNoSet.delete(clickedLot.lotNo);
      } else {
        nextSelectedLotNoSet.add(clickedLot.lotNo);
      }

      const violation = getPRD2050_R01LotPrefixViolation(
        canonicalGroupList,
        nextSelectedLotNoSet,
      );
      if (violation) {
        if (isSelected) {
          showWarning(
            `후순위 LOT(${violation.selectedLaterLot.lotNo})이 선택되어 있어 해제할 수 없습니다.`,
          );
        } else {
          showWarning(
            `선행 LOT(${violation.missingLot.lotNo})을 먼저 선택하세요.`,
          );
        }
        return;
      }

      if (isSelected) {
        removeSelectedLotNoList([clickedLot.lotNo]);
      } else {
        addSelectedLotNoList([clickedLot.lotNo]);
      }
    },
    [
      addSelectedLotNoList,
      canonicalLotGroupMap,
      removeSelectedLotNoList,
      selectedLotNoList,
    ],
  );

  /**
   * 선택 LOT 중 상세 목록과 중복되지 않는 항목만 신규 행으로 담는다.
   * 중복 판정 키는 품목코드와 LOT 번호의 조합이다.
   */
  const handleAppendClick = () => {

      //실제 선택 데이터 추출
    const selectedList = PRD2050_R01ModalList.filter((item) =>
      selectedLotNoList.includes(item.lotNo),
    );
    if (selectedList.length === 0) {
      showWarning("담을 LOT을 선택하세요.");
      return;
    }

    //FIFO 연속성 재검사
    const prefixViolation = getFirstPRD2050_R01LotPrefixViolation(
      canonicalLotGroupMap,
      new Set(selectedLotNoList),
    );
    if (prefixViolation) {
      showWarning(
        `선행 LOT(${prefixViolation.missingLot.lotNo})을 먼저 선택하세요.`,
      );
      return;
    }

    //기존 상세 데이터의 키 생성
    const detailKeySet = new Set(
      PRD2050_R01DetailList.map((item) => `${item.itemCd}|${item.lotNo}`),
    );

    //이번 선택 목록 내부 중복 검사 준비
    const selectedKeySet = new Set<string>();
    const duplicateLotNoList: string[] = [];

    //중복 LOT 제외
    const insertList = selectedList.filter((item) => {
      const key = `${item.itemCd}|${item.lotNo}`;

      //중복일 경우
      if (detailKeySet.has(key) || selectedKeySet.has(key)) {
        duplicateLotNoList.push(item.lotNo);
        return false;
      }
      selectedKeySet.add(key);
      return true;
    });

    // 제외된 중복 LOT 안내
    if (duplicateLotNoList.length > 0) {
      showWarning(
        `이미 담긴 LOT은 제외했습니다: ${Array.from(
          new Set(duplicateLotNoList),
        ).join(", ")}`,
      );
    }
    if (insertList.length === 0) {
      return;
    }

    // 정상일 경우
    insertPRD2050_R01DetailList(insertList);
    initSelectedLotNoList();
    setIsModalVisible(false);
  };

  /** 모달의 임시 선택을 비우고 화면을 닫는다. */
  const handleCloseClick = () => {
    initSelectedLotNoList();
    setIsModalVisible(false);
  };

  return (
    <ModalWrapper
      size="qhmd"
      width={1100}
      overlay
      onOverlayClick={handleCloseClick}
      onEscKeyDown={handleCloseClick}
    >
      <div className="w-full h-[8%] flex items-center px-6 bg-gray-100 font-bold">
        투입 자재 LOT 선택
      </div>
      <div className="w-full h-[78%] min-h-0 px-6 pt-1 flex flex-col ag-theme-alpine">
        <GridContextWrapper<PRD2050_R01LotDto>
          gridRef={gridRef}
          paddedGridData={paddedGridData}
          initSelected={initSelectedLotNoList}
          defaultRowData={defaultPRD2050_R01LotDto}
          baseColumnDefs={columnDefs}
          setColumnState={setColumnState}
          localStorageKey={PRD2050_R01_MODAL_COLUMN_STATE_KEY}
          enableDataHistory={false}
        >
          <Grid_Select<PRD2050_R01LotDto>
            ref={gridRef}
            rowData={paddedGridData}
            getRowId={getRowId}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowClassRules={rowClassRules}
            onRowClicked={handleRowClick}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey={PRD2050_R01_MODAL_COLUMN_STATE_KEY}
            setColumnState={setColumnState}
            overlayNoRowsTemplate={
              '<span style="padding:32px;">투입 가능한 LOT 재고가 없습니다.</span>'
            }
          />
        </GridContextWrapper>
      </div>
      <div className="w-full h-[10%] flex items-center px-6 gap-2">
        <button
          className={`${blueButtonClass} text-white font-semibold px-4 rounded h-[30px]`}
          onClick={handleAppendClick}
        >
          선택항목 담기
        </button>
        <button className="border px-4 rounded h-[30px]" onClick={handleCloseClick}>
          닫기
        </button>
      </div>
    </ModalWrapper>
  );
};
